import { Chevron } from "@/components/ui/Chevron";
import type { DeploymentStatus, SessionState } from "@/lib/api/types";

/**
 * Terminal / in-progress display for the deploy stage.
 *
 * - `deploying`            → spinner + last status message
 * - `succeeded`            → success card + link to portal
 * - `failed`               → error card with the REAL reason
 *                             (validation issues, exception, or deploy result)
 * - `cancelled`            → muted card
 *
 * Failure messaging is the T0.3 fix: previously this collapsed every
 * non-success to "The deploy failed." which was misleading when the
 * failure was at parameter validation (most common) or anywhere else
 * upstream of the actual deploy step.
 */
export function DeployStatusView({
  state,
  finalStatus,
  error,
  scanFailures,
}: {
  state: SessionState | "idle";
  finalStatus: DeploymentStatus | null;
  error: string | null;
  scanFailures: Record<string, string>;
}) {
  if (state === "deploying") {
    return (
      <Card tone="info">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
          <Chevron pulse className="mr-2" /> deploying
        </p>
        <p className="text-base font-medium">
          Bicep is running against your subscription.
        </p>
        {finalStatus?.message ? (
          <p className="mt-1 text-sm text-muted-foreground">{finalStatus.message}</p>
        ) : null}
      </Card>
    );
  }

  if (state === "succeeded") {
    return (
      <Card tone="success">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
          <Chevron className="mr-2" /> succeeded
        </p>
        <p className="text-base font-medium">Pattern deployed.</p>
        {finalStatus?.deployment_id ? (
          <p className="mt-1 text-sm text-muted-foreground">
            Deployment id:{" "}
            <span className="font-mono">{finalStatus.deployment_id}</span>
          </p>
        ) : null}
      </Card>
    );
  }

  if (state === "failed") {
    // T0.3: prefer the most specific reason we have.
    //   1. validation failures (scan_failures keyed "<pattern>:validate")
    //   2. an explicit deployment status message (real deploy failure)
    //   3. session.error from an exception caught in the orchestrator
    //   4. any other scan_failure
    //   5. last-resort generic message
    const validateEntries = Object.entries(scanFailures).filter(([k]) =>
      k.endsWith(":validate"),
    );

    if (validateEntries.length > 0) {
      return (
        <Card tone="error">
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
            <Chevron className="mr-2" /> validation failed
          </p>
          <p className="text-base font-medium mb-3">
            The parameters you submitted didn't pass the pattern's validator.
          </p>
          {validateEntries.map(([key, msg]) => {
            const pattern = key.replace(/:validate$/, "");
            const issues = msg
              .split(";")
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
            return (
              <div key={key} className="mt-3">
                <p className="font-mono text-xs text-muted-foreground mb-1">
                  {pattern}
                </p>
                <ul className="space-y-1 text-sm">
                  {issues.map((issue, i) => (
                    <li key={i} className="font-mono text-foreground/90">
                      <span className="text-destructive mr-2">×</span>
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </Card>
      );
    }

    const otherScanFailures = Object.entries(scanFailures);
    const message =
      finalStatus?.message ??
      error ??
      (otherScanFailures.length > 0
        ? `${otherScanFailures[0][0]}: ${otherScanFailures[0][1]}`
        : "The orchestration failed before reaching deploy.");

    return (
      <Card tone="error">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
          <Chevron className="mr-2" /> failed
        </p>
        <p className="text-base font-medium">{message}</p>
      </Card>
    );
  }

  if (state === "cancelled") {
    return (
      <Card tone="muted">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
          <Chevron className="mr-2" /> cancelled
        </p>
        <p className="text-base font-medium">
          Session ended before deploy. Start a new scenario to try again.
        </p>
      </Card>
    );
  }

  return null;
}

function Card({
  tone,
  children,
}: {
  tone: "info" | "success" | "error" | "muted";
  children: React.ReactNode;
}) {
  const className =
    tone === "success"
      ? "border-emerald-500/40 bg-emerald-500/5"
      : tone === "error"
      ? "border-destructive/40 bg-destructive/5"
      : tone === "muted"
      ? "border-border/60 bg-card"
      : "border-primary/40 bg-primary/5";
  return (
    <section className={`rounded-lg border-2 p-6 ${className}`}>{children}</section>
  );
}
